/**
 * Bootstrap API Client
 * Axios instance for the ticket-bootstrap service
 */

import axios from 'axios';
import { getSessionToken } from '@descope/react-sdk';
import { AppMode } from '@/lib/config/types';

interface StoredConfig {
  mode?: AppMode;
  localBaseUrl?: string;
  localTenantId?: string;
  [key: string]: unknown;
}

const getStoredConfig = (): StoredConfig | null => {
  try {
    const stored = localStorage.getItem('harmony-management-config');
    if (stored) {
      return JSON.parse(stored) as StoredConfig;
    }
  } catch (error) {
    console.error('Failed to load config from localStorage:', error);
  }
  return null;
};

export const bootstrapClient = axios.create();

export const configureBootstrapClient = (): void => {
  try {
    const config = getStoredConfig();
    const mode = config?.mode ?? AppMode.REMOTE;

    let baseUrl: string;
    if (mode === AppMode.LOCAL) {
      const localBaseUrl = config?.localBaseUrl ?? 'http://localhost:8000';
      baseUrl = `${localBaseUrl}/api/v1`;
    } else {
      baseUrl = (import.meta.env.VITE_BOOTSTRAP_API_URL as string | undefined)
        ?? `${import.meta.env.VITE_API_BASE_URL || 'https://api.dev.us1.harmony.so'}/ticket-bootstrap/api/v1`;
    }

    bootstrapClient.defaults.baseURL = baseUrl;
    bootstrapClient.interceptors.request.use((v) => {
      try {
        if (mode === AppMode.REMOTE) {
          const token = getSessionToken();
          if (token) {
            v.headers.set('Authorization', `Bearer ${token}`);
          }
        } else {
          const localTenantId = config?.localTenantId;
          if (localTenantId) {
            v.headers.set('x-tenant-id', localTenantId);
          }
        }

        if (import.meta.env.DEV && import.meta.env.VITE_FORCE_TENANT_ID) {
          v.headers.set('x-tenant-id', import.meta.env.VITE_FORCE_TENANT_ID);
        }
      } catch (error: unknown) {
        console.error('Failed to configure Bootstrap request headers:', error);
      }
      return v;
    });

    console.log(`Bootstrap client configured for ${mode} mode with baseURL: ${baseUrl}`);
  } catch (error) {
    console.error('Failed to configure Bootstrap client:', error);
  }
};

configureBootstrapClient();
